import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    Grid,
    TextField,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Card,
    CardContent,
    CardHeader,
    CardActions,
    Typography,
    Chip,
    Box,
    useTheme,
    useMediaQuery,
    Divider,
    Avatar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Container
} from "@material-ui/core";
import {
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    DeviceHub as ConnectionIcon,
    Queue as QueueIcon,
    Star as StarIcon,
    Public as PublicIcon,
    Lock as PrivateIcon,
    Timeline as TrialIcon,
    Android as IASDRIcon
} from "@material-ui/icons";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import toastUtils from "../../utils/toastUtils";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import FormattedCurrency from "../FormattedCurrency";

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh'
    },
    headerSection: {
        marginBottom: theme.spacing(4),
        textAlign: 'center'
    },
    title: {
        fontSize: '2.5rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1)
    },
    subtitle: {
        fontSize: '1.1rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3)
    },
    formCard: {
        marginBottom: theme.spacing(4),
        borderRadius: theme.spacing(2),
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
    },
    fullWidth: {
        width: '100%'
    },
    textfield: {
        width: '100%'
    },
    row: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    control: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1)
    },
    buttonContainer: {
        textAlign: 'right',
        padding: theme.spacing(1)
    },
    plansGrid: {
        marginTop: theme.spacing(3)
    },
    planCard: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.spacing(2),
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
    },
    planCardFeatured: {
        border: `2px solid ${theme.palette.primary.main}`,
        boxShadow: '0 12px 40px rgba(63, 81, 181, 0.15)'
    },
    planCardHeader: {
        textAlign: 'center',
        paddingBottom: theme.spacing(1)
    },
    planName: {
        fontSize: '1.5rem',
        fontWeight: 600,
        marginBottom: theme.spacing(1)
    },
    planPrice: {
        fontSize: '2.5rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(0.5)
    },
    planPriceUnit: {
        fontSize: '1rem',
        fontWeight: 400,
        color: theme.palette.text.secondary
    },
    featuredBadge: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        borderRadius: theme.spacing(3),
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
        fontSize: '0.75rem',
        fontWeight: 600,
        zIndex: 1
    },
    trialBadge: {
        position: 'absolute',
        top: theme.spacing(1),
        left: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
        borderRadius: theme.spacing(3),
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
        fontSize: '0.75rem',
        fontWeight: 600,
        zIndex: 1
    },
    planContent: {
        flex: 1,
        padding: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(2),
        '&:first-child': {
            marginTop: 0
        }
    },
    limitChip: {
        margin: theme.spacing(0.25),
        backgroundColor: theme.palette.grey[100],
        '& .MuiChip-label': {
            fontSize: '0.8rem',
            fontWeight: 500
        }
    },
    featureItem: {
        padding: `${theme.spacing(0.5)}px 0`,
        minHeight: 'auto'
    },
    featureIcon: {
        minWidth: theme.spacing(4),
        color: theme.palette.success.main
    },
    featureIconDisabled: {
        minWidth: theme.spacing(4),
        color: theme.palette.error.main
    },
    featureText: {
        margin: 0,
        '& .MuiTypography-body1': {
            fontSize: '0.9rem',
            fontWeight: 400
        }
    },
    cardActions: {
        padding: theme.spacing(2),
        justifyContent: 'center'
    },
    editButton: {
        width: '100%',
        borderRadius: theme.spacing(3),
        padding: theme.spacing(1.5),
        fontWeight: 600,
        textTransform: 'none',
        fontSize: '1rem',
        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
        color: 'white',
        '&:hover': {
            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        color: theme.palette.text.secondary
    },
    emptyStateIcon: {
        fontSize: '4rem',
        marginBottom: theme.spacing(2),
        opacity: 0.5
    },
    emptyStateText: {
        fontSize: '1.2rem',
        marginBottom: theme.spacing(1)
    },
    emptyStateSubtext: {
        fontSize: '1rem'
    },
    planTypeChip: {
        marginBottom: theme.spacing(1)
    }
}));

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        useIASDR: true,
        isPublic: true,
        useWhatsappOfficial: false,
        useGroups: false,
        useLandingPages: false,
        useWebChat: false,
        useAssistants: false,
        useNapp: false,
        useAsterisk: false,
        onlyApiMessage: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly'
    });

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Card className={classes.formCard} elevation={3}>
            <CardHeader 
                title={record.id ? "Editando Plano" : "Criar Novo Plano"}
                subheader="Configure os recursos e limites do plano"
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            />
            <CardContent>
                <Formik
                    enableReinitialize
                    className={classes.fullWidth}
                    initialValues={record}
                    onSubmit={(values, { resetForm }) =>
                        setTimeout(() => {
                            handleSubmit(values)
                            resetForm()
                        }, 500)
                    }
                >
                    {(values) => (
                        <Form className={classes.fullWidth}>
                            <Grid spacing={2} justifyContent="flex-start" container>

                                {/* PUBLICO */}
                                <Grid xs={12} sm={6} md={2} xl={2} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="status-selection">{i18n.t("plans.form.public")}</InputLabel>
                                        <Field
                                            as={Select}
                                            id="status-selection"
                                            label={i18n.t("plans.form.public")}
                                            labelId="status-selection-label"
                                            name="isPublic"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>Sim</MenuItem>
                                            <MenuItem value={false}>Não</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* TRIAL */}
                                <Grid xs={12} sm={6} md={2} xl={2} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="trial-selection">Trial</InputLabel>
                                        <Field
                                            as={Select}
                                            id="trial-selection"
                                            label="Trial"
                                            labelId="trial-selection-label"
                                            name="trial"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>Sim</MenuItem>
                                            <MenuItem value={false}>Não</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* TRIAL DAYS */}
                                <Grid xs={12} sm={6} md={2} xl={2} item>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("plans.form.trialDays")}
                                        name="trialDays"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        disabled={!values.values.trial}
                                    />
                                </Grid>

                                {/* NOME */}
                                <Grid xs={12} sm={6} md={6} xl={6} item>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("plans.form.name")}
                                        name="name"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                    />
                                </Grid>

                                {/* USUARIOS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("plans.form.users")}
                                        name="users"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                        type="number"
                                    />
                                </Grid>

                                {/* CONEXOES */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("plans.form.connections")}
                                        name="connections"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                        type="number"
                                    />
                                </Grid>

                                {/* FILAS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <Field
                                        as={TextField}
                                        label="Filas"
                                        name="queues"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                        type="number"
                                    />
                                </Grid>

                                {/* VALOR */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <Field
                                        as={TextField}
                                        label="Valor"
                                        name="amount"
                                        variant="outlined"
                                        className={classes.fullWidth}
                                        margin="dense"
                                        type="text"
                                    />
                                </Grid>

                                {/* WHATSAPP */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useWhatsapp-selection">Whatsapp</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useWhatsapp-selection"
                                            label="Whatsapp"
                                            labelId="useWhatsapp-selection-label"
                                            name="useWhatsapp"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* WHATSAPP OFICIAL */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useWhatsappOfficial-selection">Whatsapp Oficial</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useWhatsappOfficial-selection"
                                            label="Whatsapp Oficial"
                                            labelId="useWhatsappOfficial-selection-label"
                                            name="useWhatsappOfficial"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* FACEBOOK */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useFacebook-selection">Facebook</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useFacebook-selection"
                                            label="Facebook"
                                            labelId="useFacebook-selection-label"
                                            name="useFacebook"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* INSTAGRAM */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useInstagram-selection">Instagram</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useInstagram-selection"
                                            label="Instagram"
                                            labelId="useInstagram-selection-label"
                                            name="useInstagram"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* CAMPANHAS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useCampaigns-selection">{i18n.t("plans.form.campaigns")}</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useCampaigns-selection"
                                            label={i18n.t("plans.form.campaigns")}
                                            labelId="useCampaigns-selection-label"
                                            name="useCampaigns"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* AGENDAMENTOS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useSchedules-selection">{i18n.t("plans.form.schedules")}</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useSchedules-selection"
                                            label={i18n.t("plans.form.schedules")}
                                            labelId="useSchedules-selection-label"
                                            name="useSchedules"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* CHAT INTERNO */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useInternalChat-selection">Chat Interno</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useInternalChat-selection"
                                            label="Chat Interno"
                                            labelId="useInternalChat-selection-label"
                                            name="useInternalChat"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* API Externa */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useExternalApi-selection">API Externa</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useExternalApi-selection"
                                            label="API Externa"
                                            labelId="useExternalApi-selection-label"
                                            name="useExternalApi"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* KANBAN */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useKanban-selection">Kanban</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useKanban-selection"
                                            label="Kanban"
                                            labelId="useKanban-selection-label"
                                            name="useKanban"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* OPENAI */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useOpenAi-selection">Talk.Ai</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useOpenAi-selection"
                                            label="Talk.Ai"
                                            labelId="useOpenAi-selection-label"
                                            name="useOpenAi"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* INTEGRACOES */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useIntegrations-selection">Integrações</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useIntegrations-selection"
                                            label="Integrações"
                                            labelId="useIntegrations-selection-label"
                                            name="useIntegrations"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* IA SDR */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useIASDR-selection">IA SDR</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useIASDR-selection"
                                            label="IA SDR"
                                            labelId="useIASDR-selection-label"
                                            name="useIASDR"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* GROUPS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useGroups-selection">Grupos</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useGroups-selection"
                                            label="Grupos"
                                            labelId="useGroups-selection-label"
                                            name="useGroups"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* LANDING PAGES */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useLandingPages-selection">Landing Pages</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useLandingPages-selection"
                                            label="Landing Pages"
                                            labelId="useLandingPages-selection-label"
                                            name="useLandingPages"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* WEBCHAT */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useWebChat-selection">WebChat</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useWebChat-selection"
                                            label="WebChat"
                                            labelId="useWebChat-selection-label"
                                            name="useWebChat"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* ASSISTANTS */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useAssistants-selection">Assistentes</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useAssistants-selection"
                                            label="Assistentes"
                                            labelId="useAssistants-selection-label"
                                            name="useAssistants"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* NAPP E-COMMERCE */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useNapp-selection">E-commerce NAPP</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useNapp-selection"
                                            label="E-commerce NAPP"
                                            labelId="useNapp-selection-label"
                                            name="useNapp"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* ASTERISK VoIP */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="useAsterisk-selection">{i18n.t("asterisk.phone")}</InputLabel>
                                        <Field
                                            as={Select}
                                            id="useAsterisk-selection"
                                            label={i18n.t("asterisk.phone")}
                                            labelId="useAsterisk-selection-label"
                                            name="useAsterisk"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>

                                {/* APENAS API */}
                                <Grid xs={12} sm={6} md={3} item>
                                    <FormControl margin="dense" variant="outlined" fullWidth>
                                        <InputLabel htmlFor="onlyApiMessage-selection">Apenas API de Mensagens</InputLabel>
                                        <Field
                                            as={Select}
                                            id="onlyApiMessage-selection"
                                            label="Apenas API de Mensagens"
                                            labelId="onlyApiMessage-selection-label"
                                            name="onlyApiMessage"
                                            margin="dense"
                                        >
                                            <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                            <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                        </Field>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Box mt={3}>
                                <Grid spacing={2} justifyContent="flex-end" container>
                                    <Grid sm={3} md={2} item>
                                        <ButtonWithSpinner 
                                            className={classes.fullWidth} 
                                            loading={loading} 
                                            onClick={() => onCancel()} 
                                            variant="outlined"
                                        >
                                            {i18n.t("plans.form.clear")}
                                        </ButtonWithSpinner>
                                    </Grid>
                                    {record.id !== undefined ? (
                                        <Grid sm={3} md={2} item>
                                            <ButtonWithSpinner 
                                                className={classes.fullWidth} 
                                                loading={loading} 
                                                onClick={() => onDelete(record)} 
                                                variant="contained" 
                                                color="secondary"
                                            >
                                                {i18n.t("plans.form.delete")}
                                            </ButtonWithSpinner>
                                        </Grid>
                                    ) : null}
                                    <Grid sm={3} md={2} item>
                                        <ButtonWithSpinner 
                                            className={classes.fullWidth} 
                                            loading={loading} 
                                            type="submit" 
                                            variant="contained" 
                                            color="primary"
                                        >
                                            {i18n.t("plans.form.save")}
                                        </ButtonWithSpinner>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </CardContent>
        </Card>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    if (!records || records.length === 0) {
        return (
            <Box className={classes.emptyState}>
                <StarIcon className={classes.emptyStateIcon} />
                <Typography variant="h6" className={classes.emptyStateText}>
                    {i18n.t("plans.cards.noPlans")}
                </Typography>
                <Typography variant="body2" className={classes.emptyStateSubtext}>
                    {i18n.t("plans.cards.createFirst")}
                </Typography>
            </Box>
        )
    }

    const PlanCard = ({ plan }) => {
        const isFeatured = plan.isPublic && !plan.trial
        const isTrial = plan.trial
        
        return (
            <Card 
                className={`${classes.planCard} ${isFeatured ? classes.planCardFeatured : ''}`}
                onClick={() => onSelect(plan)}
            >
                {isFeatured && (
                    <Box className={classes.featuredBadge}>
                        {i18n.t("plans.cards.popular")}
                    </Box>
                )}
                {isTrial && (
                    <Box className={classes.trialBadge}>
                        {i18n.t("plans.cards.trial")}
                    </Box>
                )}

                <CardHeader className={classes.planCardHeader}>
                    <Box>
                        <Typography className={classes.planName}>
                            {plan.name || 'Plano sem nome'}
                        </Typography>
                        
                        <Typography className={classes.planPrice}>
                            <FormattedCurrency value={plan.amount || 0} />
                            <span className={classes.planPriceUnit}>
                                {i18n.t("plans.cards.perMonth")}
                            </span>
                        </Typography>
                        
                        <Chip 
                            size="small"
                            icon={plan.isPublic ? <PublicIcon /> : <PrivateIcon />}
                            label={plan.isPublic ? i18n.t("plans.cards.publicPlan") : i18n.t("plans.cards.privatePlan")}
                            className={classes.planTypeChip}
                            color={plan.isPublic ? "primary" : "default"}
                        />
                        
                        {isTrial && (
                            <Typography variant="body2" color="textSecondary">
                                {plan.trialDays} {i18n.t("plans.cards.trialDaysLabel")}
                            </Typography>
                        )}
                    </Box>
                </CardHeader>

                <CardContent className={classes.planContent}>
                    {/* Limites do Plano */}
                    <Typography className={classes.sectionTitle}>
                        {i18n.t("plans.cards.limits")}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        <Chip 
                            icon={<PeopleIcon />}
                            label={`${plan.users} ${i18n.t("plans.cards.userLimit")}`}
                            className={classes.limitChip}
                            size="small"
                        />
                        <Chip 
                            icon={<ConnectionIcon />}
                            label={`${plan.connections} ${i18n.t("plans.cards.connectionLimit")}`}
                            className={classes.limitChip}
                            size="small"
                        />
                        <Chip 
                            icon={<QueueIcon />}
                            label={`${plan.queues} ${i18n.t("plans.cards.queueLimit")}`}
                            className={classes.limitChip}
                            size="small"
                        />
                    </Box>

                    {/* Canais de Comunicação */}
                    <Typography className={classes.sectionTitle}>
                        {i18n.t("plans.cards.channels")}
                    </Typography>
                    <List dense>
                        <ListItem className={classes.featureItem} disableGutters>
                            <ListItemIcon className={plan.useWhatsapp ? classes.featureIcon : classes.featureIconDisabled}>
                                {plan.useWhatsapp ? <CheckIcon /> : <CloseIcon />}
                            </ListItemIcon>
                            <ListItemText 
                                primary={i18n.t("plans.cards.whatsapp")}
                                className={classes.featureText}
                            />
                        </ListItem>
                        
                        {plan.useWhatsappOfficial && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.whatsappOfficial")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        <ListItem className={classes.featureItem} disableGutters>
                            <ListItemIcon className={plan.useFacebook ? classes.featureIcon : classes.featureIconDisabled}>
                                {plan.useFacebook ? <CheckIcon /> : <CloseIcon />}
                            </ListItemIcon>
                            <ListItemText 
                                primary={i18n.t("plans.cards.facebook")}
                                className={classes.featureText}
                            />
                        </ListItem>
                        
                        <ListItem className={classes.featureItem} disableGutters>
                            <ListItemIcon className={plan.useInstagram ? classes.featureIcon : classes.featureIconDisabled}>
                                {plan.useInstagram ? <CheckIcon /> : <CloseIcon />}
                            </ListItemIcon>
                            <ListItemText 
                                primary={i18n.t("plans.cards.instagram")}
                                className={classes.featureText}
                            />
                        </ListItem>
                    </List>

                    {/* Recursos Avançados */}
                    <Typography className={classes.sectionTitle}>
                        {i18n.t("plans.cards.advanced")}
                    </Typography>
                    <List dense>
                        {plan.useCampaigns && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.form.campaigns")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useSchedules && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.form.schedules")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useInternalChat && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.internalChat")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useExternalApi && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.externalApi")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useKanban && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.kanban")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useOpenAi && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.openai")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useIntegrations && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Integrações"
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}

                        {plan.useIASDR && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <IASDRIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="IA SDR"
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useGroups && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.groups")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useLandingPages && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.landingPages")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useWebChat && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={i18n.t("plans.cards.webChat")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                        
                        {plan.useAssistants && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={i18n.t("plans.cards.assistants")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}

                        {plan.useNapp && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="E-commerce NAPP"
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}

                        {plan.useAsterisk && (
                            <ListItem className={classes.featureItem} disableGutters>
                                <ListItemIcon className={classes.featureIcon}>
                                    <CheckIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={i18n.t("asterisk.phone")}
                                    className={classes.featureText}
                                />
                            </ListItem>
                        )}
                    </List>
                </CardContent>

                <CardActions className={classes.cardActions}>
                    <ButtonWithSpinner
                        className={classes.editButton}
                        startIcon={<EditIcon />}
                        variant="contained"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(plan);
                        }}
                    >
                        {i18n.t("plans.cards.edit")}
                    </ButtonWithSpinner>
                </CardActions>
            </Card>
        )
    }

    return (
        <Grid container spacing={3} className={classes.plansGrid}>
            {records.map((plan) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={plan.id}>
                    <PlanCard plan={plan} />
                </Grid>
            ))}
        </Grid>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        useIASDR: true,
        isPublic: true,
        useWhatsappOfficial: false,
        useGroups: false,
        useLandingPages: false,
        useWebChat: false,
        useAssistants: false,
        useNapp: false,
        useAsterisk: false,
        onlyApiMessage: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly'
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toastUtils.error('Não foi possível carregar a lista de registros', 'Erro no Plano')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        console.log(data)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            toastUtils.success('Operação realizada com sucesso!', 'Plano Atualizado')
        } catch (e) {
            toastUtils.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente', 'Erro no Plano')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toastUtils.success('Operação realizada com sucesso!', 'Plano Atualizado')
        } catch (e) {
            toastUtils.error('Não foi possível realizar a operação', 'Erro no Plano')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            id: undefined,
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            useKanban: true,
            useOpenAi: true,
            useIntegrations: true,
            useIASDR: true,
            isPublic: true,
            useWhatsappOfficial: false,
            useGroups: false,
            useLandingPages: false,
            useWebChat: false,
            useAssistants: false,
            onlyApiMessage: false,
            trial: false,
            trialDays: 3,
            recurrence: 'monthly'
        })
    }

    const handleSelect = (data) => {

        let useWhatsapp = data.useWhatsapp === false ? false : true
        let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true
        let useFacebook = data.useFacebook === false ? false : true
        let useInstagram = data.useInstagram === false ? false : true
        let useCampaigns = data.useCampaigns === false ? false : true
        let useSchedules = data.useSchedules === false ? false : true
        let useInternalChat = data.useInternalChat === false ? false : true
        let useExternalApi = data.useExternalApi === false ? false : true
        let useKanban = data.useKanban === false ? false : true
        let useOpenAi = data.useOpenAi === false ? false : true
        let useIntegrations = data.useIntegrations === false ? false : true
        let useIASDR = data.useIASDR === false ? false : true
        let useGroups = data.useGroups === false ? false : true
        let useLandingPages = data.useLandingPages === false ? false : true
        let useWebChat = data.useWebChat === false ? false : true
        let useAssistants = data.useAssistants === false ? false : true
        let useNapp = data.useNapp === false ? false : true
        let useAsterisk = data.useAsterisk === false ? false : true
        let onlyApiMessage = data.onlyApiMessage === false ? false : true

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            useWhatsapp,
            useWhatsappOfficial,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useOpenAi,
            useIntegrations,
            useIASDR,
            useGroups,
            useLandingPages,
            useWebChat,
            useAssistants,
            useNapp,
            useAsterisk,
            onlyApiMessage,
            isPublic: data.isPublic,
            trial: data.trial,
            trialDays: data.trialDays,
            recurrence: data.recurrence
        })
    }

    return (
        <Container maxWidth="xl" className={classes.root}>
            <Paper className={classes.mainPaper} elevation={0}>
                {/* Form Section */}
                <PlanManagerForm
                    initialValue={record}
                    onDelete={handleOpenDeleteDialog}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    loading={loading}
                />

                {/* Plans Grid Section */}
                <PlansManagerGrid
                    records={records}
                    onSelect={handleSelect}
                />

                <ConfirmationModal
                    title="Exclusão de Registro"
                    open={showConfirmDialog}
                    onClose={() => setShowConfirmDialog(false)}
                    onConfirm={() => handleDelete()}
                >
                    Deseja realmente excluir esse registro?
                </ConfirmationModal>
            </Paper>
        </Container>
    )
}